import type {
  AcceptGuestInviteDto,
  AcceptInviteDto,
  AchievementDto,
  AuthResult,
  AuthenticatedUser,
  CosmeticSlot,
  CreateInviteDto,
  CreateQuestDto,
  CreateWorkspaceDto,
  GuildInfluenceDto,
  InventoryDto,
  InviteCreatedDto,
  InvitePreviewDto,
  LeaderboardDto,
  LeaderboardKind,
  LobbyJoinDto,
  LobbySessionsDto,
  LoginDto,
  MyRoomsDto,
  ProgressionStatsDto,
  QuestDto,
  RegisterAccountDto,
  RegisterWorkspaceDto,
  ReleaseDto,
  RoomSummaryDto,
  SetLeaderboardOptInDto,
  SetWorldThemeDto,
  ShipReleaseDto,
  UpdateAppearanceDto,
  UpdateDisplayNameDto,
  WorkspaceDto,
  WorldThemeId,
} from '@duck/contracts';
import { WORLD_THEME_IDS } from '@duck/contracts';
import { API_BASE } from '../config';
import { useAuthStore } from '../state/auth.store';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly issues?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Narrow an unknown to a known world theme id (M8) — defensive validation for the workspace read. */
function isWorldThemeId(value: unknown): value is WorldThemeId {
  return typeof value === 'string' && (WORLD_THEME_IDS as readonly string[]).includes(value);
}

/** djb2-style 32-bit hash → 8-char hex. Not cryptographic — just a stable, compact device-param digest. */
function hash32(str: string, seed: number): string {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(h, 33) ^ str.charCodeAt(i)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

let cachedFingerprint: string | null = null;

/**
 * A stable, privacy-light DEVICE FINGERPRINT (ТЗ §6): a digest of a few COARSE, stable browser params
 * (User-Agent, language, platform, screen size, timezone). Sent on login/register/refresh so the server
 * can silently extend a session ON THE SAME DEVICE — a SECONDARY signal only, never an identifier we send
 * raw (the server stores just its SHA-256) and never the sole defense. Deliberately coarse so a routine
 * browser update doesn't churn it. Memoised per page load.
 */
export function deviceFingerprint(): string {
  if (cachedFingerprint) return cachedFingerprint;
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const scr = typeof screen !== 'undefined' ? screen : ({} as Screen);
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    tz = '';
  }
  const raw = [
    nav.userAgent ?? '',
    nav.language ?? '',
    (nav as { platform?: string }).platform ?? '',
    `${scr.width ?? ''}x${scr.height ?? ''}x${scr.colorDepth ?? ''}`,
    tz,
  ].join('|');
  // Two seeds → a 16-char token (≥ FingerprintSchema's min 8), low collision without a crypto dep.
  cachedFingerprint = `${hash32(raw, 5381)}${hash32(raw, 2166136261)}`;
  return cachedFingerprint;
}

let refreshInFlight: Promise<boolean> | null = null;

/** Attempt a single refresh-token rotation; returns whether we now hold a fresh access token. */
async function tryRefresh(): Promise<boolean> {
  const { tokens, setTokens, clear } = useAuthStore.getState();
  if (!tokens?.refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Include this device's fingerprint so the server can silently extend the session here (ТЗ §6).
        body: JSON.stringify({
          refreshToken: tokens.refreshToken,
          fingerprint: deviceFingerprint(),
        }),
      });
      if (!res.ok) {
        // Only a genuine auth rejection (the refresh token is actually invalid/expired) ends the session.
        // A transient failure — gateway redeploying (5xx), a brief blip — must NOT clear auth, or one
        // unlucky background refresh would evict a live player to the entry screen (a felt "reload").
        if (res.status === 401 || res.status === 403) clear();
        return false;
      }
      const result = (await res.json()) as AuthResult;
      // A refresh only rotates tokens — the user identity is unchanged. Update ONLY the tokens (NOT
      // setAuth, which would also replace the `user` object reference and needlessly churn every
      // user-keyed effect/subscriber — including the realtime connect effect this used to re-run, which
      // tore down and rebuilt the room/voice/Pixi session every ~15 minutes).
      setTokens(result.tokens);
      return true;
    } catch {
      // Network error reaching the gateway (offline, DNS, CORS) — keep the session; the next authenticated
      // request retries. Never evict a player on a transient connectivity failure.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  /** Internal: prevents infinite refresh loops. */
  _retried?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true } = options;
  const token = auth ? useAuthStore.getState().tokens?.accessToken : undefined;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && auth && !options._retried) {
    if (await tryRefresh()) {
      return request<T>(path, { ...options, _retried: true });
    }
  }

  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new ApiError(res.status, payload.message ?? res.statusText, payload.issues);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // ── Accounts (ТЗ §2/§3): username + password. The device fingerprint rides along (ТЗ §6) so the
  // session can silently extend on this device. `confirm` is validated client-side and never sent. ──
  register: (
    dto: Pick<
      RegisterAccountDto,
      'login' | 'displayName' | 'password' | 'theme' | 'duckColor' | 'species'
    >,
  ) =>
    request<AuthResult>('/auth/register', {
      method: 'POST',
      body: { ...dto, fingerprint: deviceFingerprint() },
      auth: false,
    }),

  registerWorkspace: (dto: RegisterWorkspaceDto) =>
    request<AuthResult>('/auth/register-workspace', { method: 'POST', body: dto, auth: false }),

  acceptInvite: (dto: AcceptInviteDto) =>
    request<AuthResult>('/auth/accept-invite', { method: 'POST', body: dto, auth: false }),

  acceptGuestInvite: (dto: AcceptGuestInviteDto) =>
    request<AuthResult>('/auth/accept-guest-invite', { method: 'POST', body: dto, auth: false }),

  // ── Lobby (behind the login wall, ТЗ §1): browse every active room, then enter one as MY account ──
  // Listing + joining both require a session now. Joining re-issues a token for THIS account scoped to
  // the target room (password-checked server-side for locked rooms) and ensures that room's own progress,
  // so the same login carries SEPARATE progress per room — the caller just setAuth + go.
  listSessions: () => request<LobbySessionsDto>('/lobby/sessions'),

  joinSession: (dto: LobbyJoinDto) =>
    request<AuthResult>('/lobby/join', {
      method: 'POST',
      body: { ...dto, fingerprint: deviceFingerprint() },
    }),

  // ── Deep-link room resolution + account-owned rooms (ADR-0055) ───────────────────────────────────
  // The URL is the SOURCE OF TRUTH for which room you're in. RoomGate resolves a `/{level_type}/{key}`
  // link (slug or id) → its metadata BEFORE connecting, so it can walk you straight into an open room,
  // send you to the lobby password prompt for a locked one, or redirect a stale link (404) to the lobby.
  resolveRoom: (key: string) =>
    request<RoomSummaryDto>(`/workspaces/resolve/${encodeURIComponent(key)}`),

  // Create a brand-new room OWNED BY my account — unlimited, no one-room limit. Returns a token already
  // scoped to the new room (role OWNER), so the caller just setAuth + navigates into it.
  createWorkspace: (dto: Pick<CreateWorkspaceDto, 'name' | 'theme' | 'password'>) =>
    request<AuthResult>('/workspaces', {
      method: 'POST',
      body: { ...dto, fingerprint: deviceFingerprint() },
    }),

  // Every room MY account owns (any occupancy) — the lobby's "Your rooms" shelf, so an empty room you
  // made is always re-enterable (the live `listSessions` feed only shows rooms with ducks in them).
  myRooms: () => request<MyRoomsDto>('/workspaces/mine'),

  login: (dto: Pick<LoginDto, 'login' | 'password'>) =>
    request<AuthResult>('/auth/login', {
      method: 'POST',
      body: { ...dto, fingerprint: deviceFingerprint() },
      auth: false,
    }),

  logout: (refreshToken: string) =>
    request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, auth: false }),

  me: () => request<AuthenticatedUser>('/auth/me'),

  // Change my character's animal + tint (ADR-0024) from the lobby's "Your character" panel. Self-service
  // on my own account — no token rotation (appearance isn't a credential); returns the fresh authed user,
  // which the caller folds into the auth store so the next room entry connects with the new look.
  updateAppearance: (dto: UpdateAppearanceDto) =>
    request<AuthenticatedUser>('/auth/appearance', { method: 'PATCH', body: dto }),

  // Rename my PUBLIC display name (ADR-0024) from the lobby's "Your character" panel. Because the name is
  // carried in the access token (the realtime room reads it off the JWT on join), the server re-issues a
  // fresh token pair carrying the new name — so this returns a full AuthResult the caller folds in via
  // setAuth (unlike updateAppearance, which never rotates the token). The device fingerprint rides along
  // so the rotated session stays bound to this device (ТЗ §6).
  updateDisplayName: (dto: Pick<UpdateDisplayNameDto, 'displayName'>) =>
    request<AuthResult>('/auth/display-name', {
      method: 'PATCH',
      body: { ...dto, fingerprint: deviceFingerprint() },
    }),

  // Delete a room MY account owns (the lobby "Your rooms" shelf). Server-authoritative: ownership is
  // re-checked and the home room is refused (409). 204 on success; the caller refreshes "Your rooms".
  deleteRoom: (workspaceId: string) =>
    request<void>(`/workspaces/${encodeURIComponent(workspaceId)}`, { method: 'DELETE' }),

  invitePreview: (key: string) =>
    request<InvitePreviewDto>(`/invites/${encodeURIComponent(key)}/preview`, { auth: false }),

  createInvite: (dto: CreateInviteDto) =>
    request<InviteCreatedDto>('/invites', { method: 'POST', body: dto }),

  myWorkspace: () => request<WorkspaceDto>('/workspaces/me'),

  // ── M8 "Guilds at Scale" (emotion: Love for the World) ──────────────────────────────────────────
  // The SAME engine, re-skinned per guild. The world theme is chosen per WORKSPACE and shared by
  // everyone in that world. `GET /workspaces/me` carries the guild's theme (the client passes it on
  // join so the room skins the world for all). The owner sets it via PATCH; the realtime then
  // broadcasts the change on the room state so the world re-skins live for everyone present.
  //
  // The frozen WorkspaceDto type doesn't yet surface `theme` (a known contract gap — see report), so
  // `myWorkspaceTheme()` reads it defensively off the live response with a safe 'office' default and
  // validates it against the known theme ids — never trusting an unexpected value into the engine.
  myWorkspaceTheme: async (): Promise<WorldThemeId> => {
    const ws = (await request<WorkspaceDto>('/workspaces/me')) as WorkspaceDto & {
      theme?: unknown;
    };
    return isWorldThemeId(ws.theme) ? ws.theme : 'office';
  },

  // Owner-only: set the guild's world theme. We assume `PATCH /workspaces/me/theme {theme}` (the
  // gateway agent confirms the path); a 404/405 surfaces as an ApiError the caller handles gracefully.
  setWorldTheme: (dto: SetWorldThemeDto) =>
    request<void>('/workspaces/me/theme', { method: 'PATCH', body: dto }),

  progression: () => request<ProgressionStatsDto>('/progression/me'),

  guildInfluence: () => request<GuildInfluenceDto>('/progression/guild'),

  // NOTE: giving reputation is intentionally NOT an HTTP call. It must be proximity-verified, which
  // only the realtime world server can attest to, so it flows over the room socket
  // (RealtimeClient.sendGiveReputation → server → internal gateway grant). See ADR-0007.
  //
  // NOTE: the old `recordAction` (POST /progression/actions — the free "Complete a quest" / "Ship a
  // release" XP faucet) is GONE in the gamified-XP redesign. XP is now earned only by server-attested
  // cooperative office quests (WORK at a desk, meetings, coffee breaks…) flushed over the realtime
  // world — never a client-trusted button (ADR-0007 "no uncapped faucet"; see OFFICE_QUESTS).

  // ── M5 "Every Release Is a Raid Boss" (emotion: Pride) ──────────────────────────────────────────
  // Quests are cooperative: anyone may post one, anyone may complete one. Completing it is a positive
  // action the server attests (XP + guild influence) — never an assignment/monitoring surface.
  listQuests: () => request<QuestDto[]>('/quests'),

  createQuest: (dto: CreateQuestDto) =>
    request<QuestDto>('/quests', { method: 'POST', body: dto }),

  completeQuest: (id: string) =>
    request<QuestDto>(`/quests/${encodeURIComponent(id)}/complete`, { method: 'POST' }),

  // A Release = the raid boss. Shipping one is a guild-wide shared win; the gateway lifts guild
  // influence and the celebration itself is broadcast over the realtime room (see OfficePage).
  listReleases: () => request<ReleaseDto[]>('/releases'),

  shipRelease: (dto: ShipReleaseDto) =>
    request<ReleaseDto>('/releases', { method: 'POST', body: dto }),

  // Badges earned ONLY from server-attested positive history (read-only to the client).
  myAchievements: () => request<AchievementDto[]>('/achievements/me'),

  // Positive-only, opt-in boards (default off — appearing is a choice, never pressure).
  leaderboard: (kind: LeaderboardKind) =>
    request<LeaderboardDto>(`/leaderboards/${encodeURIComponent(kind)}`),

  setLeaderboardOptIn: (dto: SetLeaderboardOptInDto) =>
    request<void>('/leaderboards/opt-in', { method: 'POST', body: dto }),

  // ── M6 "Express Yourself" (emotion: Identity) ───────────────────────────────────────────────────
  // The cosmetic economy: a citizen spends EARNED coins to make their duck their own. Cosmetic-only,
  // never pay-to-win (Constitution Art. 1) — the server attests coins/ownership; the realtime only
  // broadcasts the resulting LOOK. The HUD's coin balance also rides `progression()` (.coins).
  cosmeticsInventory: () => request<InventoryDto>('/cosmetics/inventory'),

  // Buy a cosmetic with earned coins. The server re-checks ownership + balance + price; the wallet,
  // ownership, and equipped set come back fresh in the returned inventory.
  purchaseCosmetic: (cosmeticId: string) =>
    request<InventoryDto>('/cosmetics/purchase', { method: 'POST', body: { cosmeticId } }),

  // Equip (or, with cosmeticId null, unequip) a cosmetic in its slot. Only one per slot. Returns the
  // updated inventory so the caller can re-broadcast the equipped look over the realtime room.
  equipCosmetic: (slot: CosmeticSlot, cosmeticId: string | null) =>
    request<InventoryDto>('/cosmetics/equip', { method: 'POST', body: { slot, cosmeticId } }),
};
